import {ActionIcon, Badge, Burger, Button, Group, MantineTheme, Text, Title, Tooltip} from "@mantine/core";
import {Link, useNavigate} from "react-router-dom";
import ProfileSearch from "./ProfileSearch";
import {BrandDiscord, MoonStars, Sun, Login, Logout, InfoCircle} from "tabler-icons-react";
import useSignOut from "react-auth-kit/hooks/useSignOut";

/**
 * Props for the AppHeader component.
 * @property sidenavOpened - Whether the sidenav is currently expanded.
 * @property setSidenavOpened - Callback to toggle the sidenav open state.
 * @property theme - The current Mantine theme object for styling.
 * @property colorScheme - The active color scheme ('light' or 'dark').
 * @property toggleColorScheme - Callback to switch between light and dark modes.
 * @property isAuthenticated - Whether the user is currently logged in.
 * @property smallScreen - Whether the viewport is above the small-screen breakpoint (640px+).
 */
interface AppHeaderProps {
    sidenavOpened: boolean;
    setSidenavOpened: (o: boolean) => void;
    theme: MantineTheme;
    colorScheme: string;
    toggleColorScheme: () => void;
    isAuthenticated: boolean;
    smallScreen: boolean;
}

export default function AppHeader(
    {sidenavOpened, setSidenavOpened, theme, colorScheme, toggleColorScheme, isAuthenticated, smallScreen}: AppHeaderProps){
    return (
        <Group align="center" justify="space-between" h="100%" px="md" wrap="nowrap">
            {/* Left section: Burger (mobile) + Logo */}
            <Group gap="sm" wrap="nowrap">
                {/* Burger menu - only show on mobile (below 640px) */}
                {!smallScreen && (
                    <Burger
                        opened={sidenavOpened}
                        onClick={() => setSidenavOpened(!sidenavOpened)}
                        size="sm"
                        aria-label="Toggle navigation"
                    />
                )}
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
                    <img
                        src="/img/brand/csc-logo.png"
                        alt="CSC Logo"
                        style={{ height: '32px', width: 'auto' }}
                    />
                    {/* Brand text - only show on desktop (640px+) */}
                    {smallScreen && (
                        <div>
                            <Group gap={4}>
                                <Title
                                    order={5}
                                    c={colorScheme === 'dark' ? 'gray.4' : 'gray.7'}
                                >
                                    Counter-Strike
                                </Title>
                                <Title
                                    order={5}
                                    c={colorScheme === 'dark' ? 'blue.4' : 'blue.6'}
                                >
                                    Confederation
                                </Title>
                            </Group>
                            <Text c="dimmed" size="xs">
                                The best way to play competitively, casually.
                            </Text>
                        </div>
                    )}
                </Link>
                {/* Mobile Alpha badge - compact version for mobile (below 640px) */}
                {!smallScreen && (
                    <Badge
                        component={Link}
                        to="/about"
                        variant="light"
                        color="orange"
                        size="sm"
                        style={{ textTransform: 'none', fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
                    >
                        Alpha
                    </Badge>
                )}
            </Group>

            {/* Center section: Alpha banner - only show on desktop (640px+) */}
            {smallScreen && (
                <Group gap="xs" wrap="nowrap">
                    <Badge
                        variant="light"
                        color="orange"
                        size="lg"
                        style={{ textTransform: 'none', fontWeight: 500 }}
                    >
                        ReCore Alpha
                    </Badge>
                    <Tooltip label="Learn more about ReCore Alpha" position="bottom">
                        <ActionIcon
                            component={Link}
                            to="/about"
                            variant="subtle"
                            color="orange"
                            size="sm"
                            aria-label="About ReCore Alpha"
                        >
                            <InfoCircle size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            )}

            {/* Right section: Search + Auth + Theme toggle */}
            <Group gap="sm" wrap="nowrap">
                {/* Search - only show on desktop (640px+) */}
                {smallScreen && (
                    <ProfileSearch
                        className=""
                        style={{ width: '280px', minWidth: '200px' }}
                    />
                )}

                {/* Login/Logout button */}
                <LoginButton loggedIn={isAuthenticated} smallScreen={smallScreen} />

                {/* Theme toggle */}
                <ActionIcon
                    variant="default"
                    onClick={() => toggleColorScheme()}
                    size={36}
                    aria-label="Toggle color scheme"
                >
                    {colorScheme === 'dark' ? <Sun size={18}/> : <MoonStars size={18}/>}
                </ActionIcon>
            </Group>
        </Group>
    );
}

function LoginButton({loggedIn, smallScreen}: { loggedIn: boolean; smallScreen: boolean }) {
    const nav = useNavigate();
    const signOut = useSignOut();

    const handleLogout = () => {
        // TODO: This should be updated so that it also send a logout mutation to the backend so that the session associated with this logout can be revoked
        signOut();
        nav("/");
        window.location.reload();
    };

    if (loggedIn) {
        // Desktop: full button with text, Mobile: icon-only button
        // Note: Explicit styles needed due to Mantine Button/UnstyledButton CSS cascade issue
        return smallScreen ? (
            <Button
                onClick={handleLogout}
                leftSection={<BrandDiscord size={20}/>}
                variant="filled"
                color="blue"
                size="sm"
                style={{ backgroundColor: '#228be6', color: '#fff' }}
            >
                Logout
            </Button>
        ) : (
            <ActionIcon
                onClick={handleLogout}
                variant="filled"
                color="blue"
                size={36}
                aria-label="Logout"
                style={{ backgroundColor: '#228be6', color: '#fff' }}
            >
                <Logout size={18}/>
            </ActionIcon>
        );
    }

    // Desktop: full button with text, Mobile: icon-only button
    // Note: Explicit styles needed due to Mantine Button/UnstyledButton CSS cascade issue
    return smallScreen ? (
        <Button
            onClick={() => nav("/login")}
            leftSection={<BrandDiscord size={20}/>}
            variant="filled"
            color="blue"
            size="sm"
            style={{ backgroundColor: '#228be6', color: '#fff' }}
        >
            Login
        </Button>
    ) : (
        <ActionIcon
            onClick={() => nav("/login")}
            variant="filled"
            color="blue"
            size={36}
            aria-label="Login with Discord"
            style={{ backgroundColor: '#228be6', color: '#fff' }}
        >
            <Login size={18}/>
        </ActionIcon>
    );
}
